/**
 * streamInsight — fetch an AI insight endpoint as a Server-Sent Events stream.
 *
 * @param {string} url        - Endpoint URL (string)
 * @param {object} body       - Request body (for POST) or null (for GET)
 * @param {object} callbacks
 *   onToken(delta: string)   - called for each streamed text chunk
 *   onRetry()                - called if server signals an overlap-based retry
 *   onDone(finalText: string) - called with server-sanitized final text
 *   onError(err: Error)      - called on network or server error
 *   onStatus(text: string)   - called when server sends a status/progress message
 * @param {string} [method]   - 'POST' | 'GET' (default: body ? 'POST' : 'GET')
 * @param {object} [extraHeaders] - Additional HTTP headers (e.g. Authorization: Bearer token)
 */
export async function streamInsight(url, body, { onToken, onRetry, onDone, onError, onStatus }, method, extraHeaders) {
  const m = method || (body ? 'POST' : 'GET');
  try {
    const fetchOpts = {
      method: m,
      headers: { 'Accept': 'text/event-stream', 'Content-Type': 'application/json', ...(extraHeaders || {}) },
    };
    if (body && m !== 'GET') fetchOpts.body = JSON.stringify(body);

    const res = await fetch(url, fetchOpts);
    if (!res.ok) {
      // 402 = subscription required — invalidate cached subscription data so PaywallGate re-renders
      if (res.status === 402) {
        try {
          const { queryClient } = await import('../lib/queryClient');
          queryClient.invalidateQueries({ queryKey: ['kairos-subscription'] });
        } catch {}
      }
      let errMsg = `HTTP ${res.status}`;
      try { const j = await res.json(); errMsg = j?.error || j?.message || errMsg; } catch {}
      throw new Error(errMsg);
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    let accumulated = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop() ?? '';

      for (const line of lines) {
        const t = line.trim();
        if (!t || !t.startsWith('data: ')) continue;
        let event;
        try { event = JSON.parse(t.slice(6)); } catch { continue; }

        if (event.error) { throw new Error(event.error); }
        if (event.retry) { accumulated = ''; onRetry?.(); continue; }
        if (event.type === 'status' && event.text) { onStatus?.(event.text); continue; }
        if (event.d) { accumulated += event.d; onToken?.(event.d); continue; }
        if (event.done) {
          // Server sends sanitized final text — prefer it over accumulated raw tokens
          const finalText = event.insight ?? event.thematic ?? accumulated;
          onDone?.(finalText);
        }
      }
    }
  } catch (err) {
    onError?.(err instanceof Error ? err : new Error(String(err)));
  }
}
